import * as Sentry from '@sentry/vue';
import { pruneToDefinition } from '@/utils/savedViewDefinition';
import { viewNamesMatch } from '@/utils/savedViews';
import type { SavedViewDefinitionV2 } from '@/utils/savedViewDefinition';
import type { ColumnConfigSnapshot, SavedViewFilter, SavedViewLayout } from '@/stores/persistentTableStore';

/**
 * One-time migration of localStorage saved views onto the server (SAU-310).
 *
 * NOBODY LOSES A VIEW. An earlier draft of the plan wiped instead, on the
 * grounds that a converter is risky to run unsupervised over data nobody can
 * inspect — saved views exist only in each browser, so there is no population
 * to measure and no way to check the result. What settled it the other way is
 * that there is barely a converter: the stored shape is the same filter,
 * columnConfig and layout the pruner already takes.
 *
 * THE ORDERING RULE THIS DEPENDS ON. `normalizeTableState` deletes `savedViews`
 * from a scope the moment any read path touches it, and `readState()` runs on
 * the first render of any table page — long before a network query settles. So
 * that delete is GATED on {@link hasMigratedSavedViews}, and this module reads
 * the RAW localStorage blob rather than the hydrated store. Without both halves
 * the views are gone before the upload ever sees them, and the backup is empty.
 */

const STORE_KEY = 'persistentTableStore';
const FLAG_KEY = 'savedViews.migratedToServer';
const BACKUP_KEY = 'savedViews.wipedBackup';
const ATTEMPTS_KEY = 'savedViews.migrationAttempts';
const OWNER_KEY = 'savedViews.pendingOwnerUserId';
/** Who the parked views in BACKUP_KEY belong to, so they can be handed back. */
const PARKED_OWNER_KEY = 'savedViews.parkedOwnerUserId';

/**
 * Set when the owner stamp below was a GUESS rather than a fact.
 *
 * Nothing before SAU-310 recorded who saved a local view, so the first person to
 * sign in after the deploy is assumed to own whatever is in this browser. On a
 * shared machine that may be the wrong person. Nothing can fix that after the
 * fact — the information was never recorded — but the guess can at least be
 * visible instead of silent, which is what this key is for.
 */
const GUESSED_OWNER_KEY = 'savedViews.ownerWasGuessed';

/** How many page loads may fail before the migration stops trying. */
const MAX_MIGRATION_ATTEMPTS = 5;

function readAttemptCount(): number {
  try {
    return Number(localStorage.getItem(ATTEMPTS_KEY) ?? '0') || 0;
  } catch {
    return 0;
  }
}

/**
 * Whether the migration has already failed its way through the whole attempt
 * budget. Callers check this BEFORE doing any work of their own — the runner
 * fetches the user's existing view list first, and without this that request
 * went out on every page load forever even though the migration behind it would
 * immediately give up.
 */
export function savedViewMigrationHasGivenUp(): boolean {
  return readAttemptCount() >= MAX_MIGRATION_ATTEMPTS;
}

/**
 * Count one failed attempt, and report when the cap is reached.
 *
 * EVERY failure path calls this, which is the point. It used to be counted in one
 * branch only — the upload loop — so the other three paths (a backup write that
 * throws, and the runner's two early returns) reset the in-page guard and retried
 * on the very next load, forever. A browser near its localStorage quota reported
 * the same exception to Sentry on every page view: the unbounded shape CLAUDE.md
 * rules out, from the code that was added to bound it.
 *
 * @param canGiveUp whether reaching the cap may set the migrated flag. Only true
 *   when a verified backup exists — setting the flag lets `normalizeTableState`
 *   start deleting the local views, so doing it without a backup is the one state
 *   there is no way back from.
 */
export function recordFailedMigrationAttempt(summary: string, canGiveUp: boolean): void {
  const attempts = readAttemptCount() + 1;
  try {
    localStorage.setItem(ATTEMPTS_KEY, String(attempts));
  } catch {
    // Not being able to count means it retries a few more times. Harmless.
  }

  if (attempts < MAX_MIGRATION_ATTEMPTS) {
    return;
  }

  Sentry.captureException(
    new Error(`Saved-view migration gave up after ${attempts} attempts: ${summary}`),
  );
  if (canGiveUp) {
    // The backup is written, so nothing is lost — it is recoverable by support
    // rather than retried forever.
    markMigrated();
  }
}

export type SavedViewMigrationOutcome =
  | 'already-done'
  | 'nothing-to-do'
  | 'migrated'
  | 'partial'
  | 'blocked';

/** The localStorage shape of a saved view, as it was before SAU-310. */
interface LegacySavedView {
  id: string;
  name: string;
  filter: SavedViewFilter;
  columnConfig: ColumnConfigSnapshot;
  layout?: SavedViewLayout;
}

/** One view lifted out of the blob, with the scope it belonged to. */
export interface PendingSavedViewUpload {
  entity: string;
  name: string;
  definition: SavedViewDefinitionV2;
}

/**
 * Whether the migration has already run in this browser.
 *
 * Read by `normalizeTableState`, which is why it is a bare synchronous
 * localStorage read with no store dependency — it runs on the hot read path.
 * The flag lives OUTSIDE the persisted blob on purpose: a flag inside the thing
 * being rewritten couples its durability to the very write that might fail.
 */
export function hasMigratedSavedViews(): boolean {
  try {
    return localStorage.getItem(FLAG_KEY) === 'true';
  } catch {
    // Private windows and blocked-site-data settings throw on access. Treating
    // that as "not migrated" is the safe direction: the migration re-runs and
    // is idempotent, where the opposite would strip views that never uploaded.
    return false;
  }
}

function markMigrated(): void {
  try {
    localStorage.setItem(FLAG_KEY, 'true');
  } catch {
    // If the flag cannot be stored the migration simply runs again next load.
    // It is idempotent — name collisions are skipped, not suffixed — so a
    // repeat costs a few requests rather than duplicate views.
  }
}

/**
 * Every saved view in the raw blob, flattened across scopes and pruned into the
 * stored definition format. Exported for tests; the caller uses `migrate`.
 */
export function collectLegacySavedViews(rawBlob: string | null): PendingSavedViewUpload[] {
  if (!rawBlob) {
    return [];
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawBlob);
  } catch {
    // A corrupt blob is not recoverable and not worth failing the app over.
    // Nothing to migrate; the flag still gets set so this is not retried forever.
    return [];
  }

  const tableStates = (parsed as { tableStates?: Record<string, unknown> } | null)?.tableStates;
  if (!tableStates || typeof tableStates !== 'object') {
    return [];
  }

  const pending: PendingSavedViewUpload[] = [];
  for (const [entity, scope] of Object.entries(tableStates)) {
    const savedViews = (scope as { savedViews?: unknown } | null)?.savedViews;
    if (!savedViews || typeof savedViews !== 'object' || Array.isArray(savedViews)) {
      continue;
    }
    for (const candidate of Object.values(savedViews as Record<string, unknown>)) {
      const view = candidate as LegacySavedView;
      if (!view || typeof view.name !== 'string' || view.name.trim() === '' || !view.columnConfig) {
        continue;
      }
      pending.push({
        entity,
        name: view.name,
        definition: pruneToDefinition(entity, view.columnConfig, view.filter, view.layout),
      });
    }
  }
  return pending;
}

/** Strip the two retired keys from every scope in the blob. */
function blobWithoutSavedViews(rawBlob: string): string | null {
  try {
    const parsed = JSON.parse(rawBlob) as { tableStates?: Record<string, Record<string, unknown>> };
    const tableStates = parsed.tableStates;
    if (!tableStates) {
      return null;
    }
    for (const scope of Object.values(tableStates)) {
      // Surgical, never removeItem: this blob also holds column layouts, page
      // sizes, map-card settings and live filters that have nothing to do with
      // saved views, and dropping the whole key would destroy all of it.
      delete scope.savedViews;
      delete scope.activeSavedViewId;
    }
    return JSON.stringify(parsed);
  } catch {
    return null;
  }
}

/**
 * Whether this browser is still holding saved views that have not reached the
 * server. The one reader of the store key outside `migrate`, so callers do not
 * have to know the key or handle a blocked localStorage.
 */
export function hasPendingSavedViews(): boolean {
  try {
    return collectLegacySavedViews(localStorage.getItem(STORE_KEY)).length > 0;
  } catch {
    return false;
  }
}

/**
 * Who the not-yet-migrated local views belong to.
 *
 * WHY OWNERSHIP IS TRACKED AT ALL. `persistentTableStore` is a browser-wide
 * localStorage key with no user in it, so on a shared machine one person's local
 * views would be uploaded into the NEXT person's account: A stops using the app,
 * B logs in, B's first saved-views query settles, and the migration POSTs every
 * one of A's views as B — names and filter values included, which B can then
 * publish to the shared library.
 *
 * This used to be prevented by DESTROYING the pending views at logout, from
 * `clearTokens`. That fired on the ordinary case as well: `services/http.ts`
 * calls `clearTokens` whenever a 401's refresh attempt fails, which is what an
 * overnight-expired refresh token does. One user, one machine, nobody else
 * involved — and their views were stripped into a backup key that nothing in the
 * app ever reads, then reported as "nothing to migrate" the moment they logged
 * back in.
 *
 * So the question is asked where the UPLOAD happens instead of where the session
 * ends, and nothing is destroyed to answer it. It is also a stronger check: the
 * logout clear only helped when A actually logged out, while this covers A's
 * session simply expiring.
 *
 * THE FIRST CLAIMANT IS A GUESS AND CANNOT BE ANYTHING ELSE. Nothing before
 * SAU-310 recorded who saved a local view, so the owner key is null on every
 * browser the first time this runs — and that first run is `authStore.loadUser`,
 * on the router's auth guard, not the migration. Whoever signs in first after the
 * deploy is therefore taken to be the owner. On a shared machine that may be the
 * wrong person, and their filter values are then uploaded into an account that is
 * not theirs. This check protects the SECOND user onwards, which is all that can
 * be protected after the fact. The guess is recorded so the migration can tell
 * that user what was carried over rather than moving it in silence.
 *
 * @returns 'claimed' when nobody held them and this user now does, 'mine' when
 *   they already did, 'someone-else' when they belong to a different account.
 */
export function claimPendingSavedViews(userId: number): 'claimed' | 'mine' | 'someone-else' | 'nothing-pending' {
  // Called from every authenticated load, so it must be cheap and must not
  // stamp a browser that has nothing to stamp.
  if (!hasPendingSavedViews()) {
    return 'nothing-pending';
  }

  let owner: string | null;
  try {
    owner = localStorage.getItem(OWNER_KEY);
  } catch {
    // Cannot read, so cannot prove they are this user's. Fail closed.
    return 'someone-else';
  }

  if (owner === String(userId)) {
    return 'mine';
  }
  if (owner !== null) {
    return 'someone-else';
  }

  try {
    localStorage.setItem(OWNER_KEY, String(userId));
    // Recorded next to the stamp, not returned: the first caller is
    // authStore.loadUser, which discards the return value, so by the time the
    // migration runs this function already answers 'mine' and the fact that it
    // ever guessed would otherwise be gone.
    localStorage.setItem(GUESSED_OWNER_KEY, String(userId));
  } catch {
    // Unstamped views are re-claimed on the next load. Harmless on a
    // single-user machine, and the shared-machine window is one page load.
  }
  return 'claimed';
}

/**
 * Hand parked views back to the account they were stamped to.
 *
 * The mirror of {@link parkPendingSavedViews}. When the owner signs in again on
 * this browser, their views are spliced back into the live blob, the migrated
 * flag — which B's run set browser-wide — is cleared, and the ownership stamp
 * is restored, so the ordinary migration then runs for them. Called BEFORE the
 * runner's flag check, because the flag is exactly what would otherwise stop it.
 *
 * @returns the blob the views were restored into, or null when nothing was
 *   handed back. The caller passes that blob to {@link migrateLocalSavedViews}
 *   rather than letting it re-read localStorage: pinia-plugin-persistedstate
 *   rewrites the same key from IN-MEMORY state on every store mutation, and the
 *   in-memory state no longer carries `savedViews` — so a single column click
 *   between this write and the migration's read would erase what was restored,
 *   and the migration would then find nothing and mark the browser migrated.
 */
export function unparkSavedViewsFor(userId: number): string | null {
  try {
    if (localStorage.getItem(PARKED_OWNER_KEY) !== String(userId)) {
      return null;
    }
    const backup = localStorage.getItem(BACKUP_KEY);
    if (!backup) {
      localStorage.removeItem(PARKED_OWNER_KEY);
      return null;
    }

    const parked = JSON.parse(backup) as { tableStates?: Record<string, Record<string, unknown>> };
    const live = JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}') as {
      tableStates?: Record<string, Record<string, unknown>>;
    };
    live.tableStates ??= {};
    for (const [entity, scope] of Object.entries(parked.tableStates ?? {})) {
      if (!scope?.savedViews) {
        continue;
      }
      // Only the two retired keys travel back; the live scope keeps the column
      // layout, page size and filters it has now.
      live.tableStates[entity] = {
        ...(live.tableStates[entity] ?? {}),
        savedViews: scope.savedViews,
        activeSavedViewId: scope.activeSavedViewId,
      };
    }
    const restored = JSON.stringify(live);
    localStorage.setItem(STORE_KEY, restored);

    // The flag has to go or the migration below will not run at all. The PARKED
    // STAMP does NOT: it is the only record of whose views these are, and
    // clearing it here meant a run that failed partway could never be retried.
    // The persist plugin rewrites this key from in-memory state on the next
    // store mutation, and memory has no savedViews — so the restore would be
    // erased with nothing left pointing at the backup. Cleared on success, by
    // `clearParkedOwner`.
    localStorage.removeItem(FLAG_KEY);
    // The ATTEMPT COUNTER stays. Clearing it here looked like it belonged beside the
    // flag and did not: this function runs on EVERY load while the parked stamp is
    // still set, and the stamp is only cleared once the migration succeeds. So a
    // migration that can never succeed — one view for an entity whose permission was
    // revoked — had its budget reset before it was read, every single time. Replayed
    // twelve times: the counter never passed one, and each load re-uploaded every view
    // and raised two Sentry events, forever. That is the unbounded shape the counter
    // exists to prevent, coming from the code that resets it.
    localStorage.setItem(OWNER_KEY, String(userId));
    // The backup stays until the migration has landed them: migrate() never
    // overwrites an existing backup, and strips it only on 'migrated'.
    return restored;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
}

/**
 * Did this user's views arrive by a guess rather than by a record?
 *
 * True only for the account that was first to sign in on this browser after the
 * deploy, and only while its views have not yet been reported. The migration uses
 * it to say what was carried over.
 */
export function ownerWasGuessedFor(userId: number): boolean {
  try {
    return localStorage.getItem(GUESSED_OWNER_KEY) === String(userId);
  } catch {
    return false;
  }
}

/** Forget the guess, once the user has been told about it. */
export function clearGuessedOwner(): void {
  try {
    localStorage.removeItem(GUESSED_OWNER_KEY);
  } catch {
    // A stamp that cannot be removed means the notice repeats on the next load.
    // Noisy, not harmful, and unreachable where removeItem works at all.
  }
}

/**
 * Forget that anything was parked. Called only once the migration reports that
 * every view reached the server; until then the stamp is what lets a failed run
 * be retried on the next sign-in.
 */
export function clearParkedOwner(): void {
  try {
    localStorage.removeItem(PARKED_OWNER_KEY);
  } catch {
    // A blocked localStorage. The next unpark is idempotent, so a stamp that
    // outlives its backup costs one wasted splice and nothing else.
  }
}

/**
 * Set aside local views that belong to a DIFFERENT account, so they are neither
 * uploaded into this one nor left to be re-offered on every page load.
 *
 * Moved to a backup key rather than deleted: they are somebody's curated filters,
 * and a support-recoverable copy costs one localStorage key. The migrated flag is
 * SET, because for this user there is genuinely nothing to carry over and the
 * alternative is re-parking the same blob on every load.
 */
export function parkPendingSavedViews(): void {
  try {
    const rawBlob = localStorage.getItem(STORE_KEY);
    const hasViews = !!rawBlob && collectLegacySavedViews(rawBlob).length > 0;
    if (hasViews && !localStorage.getItem(BACKUP_KEY)) {
      localStorage.setItem(BACKUP_KEY, rawBlob as string);
    }
    // Remembered so the owner gets them BACK — and remembered WHETHER OR NOT this
    // call wrote the backup. If the owner's own migration had already failed
    // partway (an upload refused), migrate() had written the backup itself, and
    // tying the stamp to "we wrote the backup" left exactly that owner stranded:
    // one transient failure, B logs in, and A's views sit in a key nothing reads
    // behind a migrated flag that stops A's runner from ever looking.
    const owner = hasViews ? localStorage.getItem(OWNER_KEY) : null;
    if (owner !== null) {
      localStorage.setItem(PARKED_OWNER_KEY, owner);
    }
    if (rawBlob) {
      const cleaned = blobWithoutSavedViews(rawBlob);
      if (cleaned !== null) {
        localStorage.setItem(STORE_KEY, cleaned);
      }
    }
    localStorage.removeItem(OWNER_KEY);
    localStorage.removeItem(ATTEMPTS_KEY);
    markMigrated();
  } catch (error) {
    // A blocked or full localStorage. Nothing here is recoverable by retrying,
    // and the migration's own guards keep the views from being uploaded either
    // way — but this is not an expected outcome, so it is not swallowed.
    Sentry.captureException(error);
  }
}

/**
 * Run the migration. Called once, on the FIRST SUCCESSFUL saved-views query
 * settle — never at bootstrap. If the backend is not deployed yet the query
 * fails with `retry: false` and this never fires, so a frontend that ships
 * ahead of its backend touches nothing.
 *
 * @param upload posts one view and resolves when the server has it. Throwing
 *   marks that view failed; the whole set is then retried on the next load. A name
 *   the server already holds comes back as a 409, which the caller treats as a
 *   skip rather than a failure.
 */
export async function migrateLocalSavedViews(
  upload: (view: PendingSavedViewUpload) => Promise<void>,
  rawBlobOverride: string | null = null,
): Promise<SavedViewMigrationOutcome> {
  if (hasMigratedSavedViews()) {
    return 'already-done';
  }

  // One guard for every failure path. Whatever went wrong last time — a failed
  // upload, a backup that would not write, a list this browser could not fetch —
  // it has now gone wrong MAX_MIGRATION_ATTEMPTS times, and the giving-up was
  // reported when the count reached the cap.
  if (savedViewMigrationHasGivenUp()) {
    return 'blocked';
  }

  let rawBlob: string | null;
  try {
    // The override is the blob unparkSavedViewsFor just restored — see its note
    // on why the caller must not trust a re-read of localStorage for it.
    rawBlob = rawBlobOverride ?? localStorage.getItem(STORE_KEY);
  } catch (error) {
    Sentry.captureException(error);
    // No backup was written, so the flag must NOT be set: the local views are
    // still the only copy and normalizeTableState would start deleting them.
    recordFailedMigrationAttempt('localStorage could not be read', false);
    return 'blocked';
  }

  const pending = collectLegacySavedViews(rawBlob);
  if (pending.length === 0 || rawBlob === null) {
    // Nothing to carry over. Set the flag so the normalize drop can start
    // tidying, and so this does not re-read the blob on every load forever.
    markMigrated();
    return 'nothing-to-do';
  }

  // Back up BEFORE uploading, and verify the backup by reading it back. A
  // backup that silently failed to write is worse than none, because it is the
  // thing standing between a converter bug and unrecoverable data.
  //
  // Never write an EMPTY backup: that is what makes a two-tab race safe, since
  // the losing tab has nothing to say and must not overwrite the winner's copy.
  // Remembered so the success path below knows whether this backup is OURS to
  // delete. A backup somebody else wrote belongs to them — the other tab in the
  // race above, or a parked owner waiting to sign back in.
  let wroteOurOwnBackup = false;
  try {
    const existingBackup = localStorage.getItem(BACKUP_KEY);
    if (!existingBackup) {
      wroteOurOwnBackup = true;
      localStorage.setItem(BACKUP_KEY, rawBlob);
      const readBack = localStorage.getItem(BACKUP_KEY);
      if (readBack === null || readBack.length !== rawBlob.length) {
        throw new Error('Saved-view backup did not read back at the length it was written.');
      }
    }
  } catch (error) {
    // No upload, no clear, no flag — the next load retries the whole thing.
    Sentry.captureException(error);
    // ...but only a bounded number of times. A browser near its quota throws here
    // on every single load, and the backup doubles the blob's size, so this is the
    // path most likely to fail permanently. `false`: without a backup, giving up
    // must not set the flag.
    recordFailedMigrationAttempt('the local backup could not be written', false);
    return 'blocked';
  }

  // SKIP a name that is already taken for that entity, rather than suffixing it.
  //
  // Import suffixes because a collision there means two genuinely different views.
  // A collision HERE almost always means the same view arriving from the user's
  // second browser, and suffixing would hand every multi-device user a pile of
  // "Online players (2)". Skipping is also what makes the retry below idempotent:
  // a partial run can repeat without duplicating what already landed.
  //
  // THE SERVER ANSWERS THE COLLISION QUESTION. `SavedViewController::store` refuses
  // a name it already holds with a 409, and the caller's upload() treats that as a
  // skip. This used to be preceded by a GET of the user's entire saved-view list,
  // from back when store was a flat updateOrCreate and an upload could silently
  // REPLACE a view saved on another machine. That is no longer true, and the
  // pre-fetch had become the migration's most fragile step: five failed GETs spent
  // the whole attempt budget and stranded a browser's views permanently, without
  // ever attempting a single upload that would have landed or been safely refused.
  //
  // What remains is only the blob's own duplicates: a browser holding both "Report"
  // and "report" for one entity would otherwise send both, and the second would come
  // back as a 409 rather than being skipped. Names go in as they upload.
  // `viewNamesMatch` rather than a bare lowercase comparison, because the server's
  // index folds accents too.
  const taken: Record<string, string[]> = {};
  const isTaken = (view: PendingSavedViewUpload): boolean =>
    (taken[view.entity] ?? []).some((name) => viewNamesMatch(name, view.name));

  const failures: unknown[] = [];
  for (const view of pending) {
    if (isTaken(view)) {
      continue;
    }
    try {
      await upload(view);
      (taken[view.entity] ??= []).push(view.name);
    } catch (error) {
      failures.push(error);
    }
  }

  if (failures.length > 0) {
    // Bounded, not endless. One view the user can no longer save — an entity
    // whose permission was revoked since — fails with a 422 that will never heal,
    // and without a ceiling the whole set is re-POSTed on every page load for as
    // long as that browser lives. CLAUDE.md: bound it, never drop the signal.
    //
    // `true`: the verified backup above was written, so giving up here may set the
    // flag. Nothing is lost, it is recoverable by support rather than retried.
    // The first failure travels with the count. Only `.length` used to be read,
    // so a permission refusal, a 500 and a size rejection all arrived as
    // "1 view would not upload" — a report nobody could act on without
    // reproducing it. CLAUDE.md's rule is to bound the volume, not drop the
    // signal, so one exception goes with the summary rather than all of them.
    Sentry.captureException(failures[0], {
      extra: { failureCount: failures.length, pendingCount: pending.length },
    });
    recordFailedMigrationAttempt(
      `${failures.length} of ${pending.length} view(s) would not upload`,
      true,
    );
    if (hasMigratedSavedViews()) {
      return 'blocked';
    }

    // A half-migrated user with the flag set is the one state there is no way
    // back from, so nothing is cleared. The retry is safe because a name that
    // already landed is skipped rather than re-sent, and the server refuses a
    // duplicate rather than overwriting it.
    Sentry.captureException(
      new Error(
        `Saved-view migration uploaded ${pending.length - failures.length}/${pending.length}; leaving localStorage intact for retry.`,
      ),
      { extra: { failureCount: failures.length } },
    );
    return 'partial';
  }

  // Re-read rather than reuse `rawBlob`. The uploads are sequential and take
  // seconds; pinia-plugin-persistedstate rewrites this same key on every store
  // mutation, so a user who resized a column or changed the page size while they
  // ran had those changes reverted by writing back the pre-upload copy. The blob
  // also carries live filters and map-card settings, which is why the strip is
  // surgical in the first place.
  let latestBlob: string | null;
  try {
    latestBlob = localStorage.getItem(STORE_KEY);
  } catch {
    latestBlob = null;
  }

  const cleaned = blobWithoutSavedViews(latestBlob ?? rawBlob);
  if (cleaned !== null) {
    try {
      localStorage.setItem(STORE_KEY, cleaned);
    } catch (error) {
      // Everything is on the server, so this is cosmetic — the stale keys are
      // inert data nothing reads. Still worth hearing about.
      Sentry.captureException(error);
    }
  }

  // The views are on the server, so OUR pre-migration copy has nothing left to
  // protect and is deleted — which is what the comment on BACKUP_KEY already
  // promised and nothing did.
  //
  // Three things followed from never removing it. A full copy of every view name
  // and every filter value stayed in the browser indefinitely, readable by
  // whoever used the machine next. The whole point of stripping `savedViews` from
  // the blob is to shrink it, and the identical bytes stayed under the backup
  // key, so nothing was actually reclaimed — on the path the file's own comment
  // calls "most likely to fail permanently" because the backup doubles the size.
  // And parkPendingSavedViews only writes a backup when there is not one already,
  // so a stale one made a later park keep the OLDER blob and hand back the wrong
  // views.
  //
  // TWO CONDITIONS, both about not deleting somebody else's copy. `wroteOurOwnBackup`
  // is false when the other tab in the race above got there first, and that tab may
  // still be uploading. A recorded parked owner means the backup is the only copy of
  // views waiting for someone to sign back in — see unparkSavedViewsFor. And this is
  // the success path alone: a partial or blocked outcome leaves the backup exactly
  // where it is, because then it is still the only copy of something not yet sent.
  try {
    if (wroteOurOwnBackup && localStorage.getItem(PARKED_OWNER_KEY) === null) {
      localStorage.removeItem(BACKUP_KEY);
    }
  } catch (error) {
    // A blocked localStorage. Everything is on the server, so this is cosmetic —
    // but it is not an expected outcome, so it is not swallowed.
    Sentry.captureException(error);
  }

  markMigrated();
  return 'migrated';
}
